import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { api } from "../lib/api";

export default function NotificationsBell() {
    const navigate = useNavigate();
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const ref = useRef(null);

    const loadCount = async () => {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setCount(data.count || 0);
        } catch {
            // unauthenticated or offline — ignore
        }
    };

    const loadItems = async () => {
        try {
            const { data } = await api.get("/notifications", { params: { limit: 20 } });
            setItems(data || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => {
        loadCount();
        const i = setInterval(loadCount, 30000);
        return () => clearInterval(i);
    }, []);

    useEffect(() => {
        if (!open) return;
        loadItems();
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const onClickItem = async (n) => {
        try {
            await api.post(`/notifications/${n.id}/read`);
        } catch {
            // ignore
        }
        setOpen(false);
        setCount((c) => Math.max(0, c - (n.read ? 0 : 1)));
        // Navigate to the project containing the file; deep link via hash to focus the file
        navigate(`/projects/${n.project_id}?file=${n.file_id}`);
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setCount(0);
            setItems((prev) => prev.map((i) => ({ ...i, read: true })));
        } catch {
            // ignore
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="relative p-2 text-zinc-700 hover:text-zinc-900 rounded-md hover:bg-zinc-100"
                aria-label="Meldingen"
                data-testid="notifications-bell"
            >
                <Bell className="w-5 h-5" />
                {count > 0 && (
                    <span
                        className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-[#0044FF] text-white text-[10px] font-mono font-bold rounded-full flex items-center justify-center"
                        data-testid="notifications-count"
                    >
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-12 w-80 max-w-[calc(100vw-1rem)] bg-white border border-zinc-200 rounded-md shadow-xl z-50 overflow-hidden animate-fade-in"
                    data-testid="notifications-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
                        <div className="font-display text-base font-bold tracking-tight">Meldingen</div>
                        {items.some((i) => !i.read) && (
                            <button
                                onClick={markAllRead}
                                className="text-xs text-[#0044FF] hover:underline"
                                data-testid="notifications-read-all"
                            >
                                Markeer alles gelezen
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="px-4 py-10 text-center text-sm text-zinc-500">
                                Geen meldingen.
                            </div>
                        ) : (
                            <ul>
                                {items.map((n) => (
                                    <li key={n.id}>
                                        <button
                                            onClick={() => onClickItem(n)}
                                            className={`w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-0 ${
                                                n.read ? "" : "bg-blue-50/40"
                                            }`}
                                            data-testid={`notification-${n.id}`}
                                        >
                                            <div className="flex items-start gap-2">
                                                {!n.read && (
                                                    <span className="w-2 h-2 mt-1.5 rounded-full bg-[#0044FF] shrink-0" />
                                                )}
                                                <div className="min-w-0">
                                                    <div className="text-sm">
                                                        <span className="font-medium">{n.author}</span>{" "}
                                                        <span className="text-zinc-500">
                                                            {n.type === "feedback" ? "gaf feedback" : "plaatste een opmerking"}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-zinc-700 line-clamp-2 mt-0.5">
                                                        {n.preview}
                                                    </p>
                                                    <div className="text-[10px] font-mono uppercase text-zinc-500 mt-1">
                                                        {new Date(n.created_at).toLocaleString("nl-BE")}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
